import { create } from "zustand";
import type { PqEvent } from "@/lib/types";

// Keyed by event_id so duplicate publishes (HTTP keep + MQTT disposition)
// collapse to one row. Promote supersedes keep, keep supersedes drop.
const DECISION_RANK: Record<NonNullable<PqEvent["decision"]>, number> = {
  discard_non_swell: 0,
  drop: 1,
  keep: 2,
  promoted: 3,
};

const MAX_EVENTS = 500;

interface EventQueueState {
  events: Map<string, PqEvent>;
  ingest: (ev: PqEvent) => void;
  setMany: (evs: PqEvent[]) => void;
  clear: () => void;
}

function shouldSupersede(incoming: PqEvent, existing: PqEvent | undefined): boolean {
  if (!existing) return true;
  const a = DECISION_RANK[incoming.decision ?? "keep"];
  const b = DECISION_RANK[existing.decision ?? "keep"];
  if (a > b) return true;
  if (a < b) return false;
  // Same decision class — newer trigger_ts wins.
  return (incoming.trigger_ts ?? "") >= (existing.trigger_ts ?? "");
}

export const useEventQueue = create<EventQueueState>((set) => ({
  events: new Map(),
  ingest: (ev) =>
    set((state) => {
      const existing = state.events.get(ev.event_id);
      if (!shouldSupersede(ev, existing)) return state;
      const next = new Map(state.events);
      next.delete(ev.event_id); // re-insert to push to tail (Map preserves insertion order)
      next.set(ev.event_id, { ...existing, ...ev });
      while (next.size > MAX_EVENTS) {
        const oldest = next.keys().next().value;
        if (oldest === undefined) break;
        next.delete(oldest);
      }
      return { events: next };
    }),
  setMany: (evs) =>
    set(() => {
      const m = new Map<string, PqEvent>();
      for (const ev of evs) m.set(ev.event_id, ev);
      return { events: m };
    }),
  clear: () => set({ events: new Map() }),
}));
